import { Controller } from "stimulus";

const OPEN_EVENT = "utilities--dropdown:open";
// Gap between the trigger and an anchored menu, and the margin it keeps from the viewport edge.
const MENU_GAP = 5;

export default class extends Controller {
  static targets = ["menu"];
  // anchor: "fixed" takes the menu out of the flow and places it against its trigger. Opt-in, for a
  // menu inside a container that clips its overflow (the conversation list column) — an absolutely
  // positioned menu is cut there, a fixed one is not.
  static values = { anchor: String };

  connect() {
    this.boundClose = this.closeOnOutside.bind(this);
    // Close this dropdown whenever another one opens, so only a single
    // utilities--dropdown menu is ever open at the same time.
    this.boundCloseOnOther = this.closeOnOther.bind(this);
    this.boundAnchor = this.placeMenu.bind(this);
    window.addEventListener(OPEN_EVENT, this.boundCloseOnOther);
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.menuTarget.classList.contains('hidden')) {
      this.open(event.currentTarget);
    } else {
      this.close();
    }
  }

  open(trigger) {
    // Announce the opening so any other open dropdown closes itself first.
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source: this.element } }));
    this.menuTarget.classList.remove('hidden');
    if (this.anchored) {
      this.trigger = trigger || this.element;
      this.placeMenu();
      window.addEventListener('scroll', this.boundAnchor, true);
      window.addEventListener('resize', this.boundAnchor);
    }
    document.addEventListener('click', this.boundClose);
  }

  close() {
    this.menuTarget.classList.add('hidden');
    if (this.anchored) {
      this.menuTarget.style.position = this.menuTarget.style.top = this.menuTarget.style.left = this.menuTarget.style.minWidth = '';
      window.removeEventListener('scroll', this.boundAnchor, true);
      window.removeEventListener('resize', this.boundAnchor);
    }
    document.removeEventListener('click', this.boundClose);
  }

  get anchored() {
    return this.anchorValue === 'fixed';
  }

  // Under the trigger, pulled left when it would leave the viewport on the right.
  placeMenu() {
    if (!this.trigger) return;

    const trigger = this.trigger.getBoundingClientRect();
    const menu = this.menuTarget;
    menu.style.position = 'fixed';
    menu.style.top = `${trigger.bottom + MENU_GAP}px`;
    menu.style.left = `${trigger.left}px`;
    // Out of the flow the menu has no parent to size it: a percentage width would follow the
    // viewport, so the trigger sets the floor.
    menu.style.minWidth = `${trigger.width}px`;
    const overflowing = menu.getBoundingClientRect().right - (window.innerWidth - MENU_GAP);
    if (overflowing > 0) menu.style.left = `${Math.max(MENU_GAP, trigger.left - overflowing)}px`;
  }

  closeOnOther(event) {
    if (event.detail && event.detail.source !== this.element) this.close();
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  disconnect() {
    document.removeEventListener('click', this.boundClose);
    window.removeEventListener(OPEN_EVENT, this.boundCloseOnOther);
    window.removeEventListener('scroll', this.boundAnchor, true);
    window.removeEventListener('resize', this.boundAnchor);
  }
}
